import {
  Avatar,
  ChannelIcon,
  Dialog,
  ErrorState,
  Loading,
  PriorityBadge,
  SectionBoundary,
  Select,
  StatusBadge,
  useFormat,
} from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BadgeCheck, Briefcase, CheckSquare, GitMerge, Handshake, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';
import { CRM_KEYS, type TaskRow } from '../crm/data';
import { Field, NewCaseDialog, NewDealDialog, NewTaskDialog } from '../crm/forms';
import { TaskList } from '../crm/TaskList';
import { Notes, Timeline } from '../crm/Timeline';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { ConsentPanel } from '../crm/Consent';
import { PurchasesPanel } from '../retail/Purchases';
import { handleLabel } from '../crm/handles';
import { MergeHistory, MergeWithDialog } from './Duplicates';
import { IDENTITY_CHANNEL } from './People';

export interface PersonDetail {
  person: {
    id: string;
    displayName: string;
    status?: 'active' | 'merged' | 'erased';
    mergedInto?: string | null;
    nameArabic: string | null;
    jobTitle: string | null;
    lifecycleStage: string;
    ownerName: string | null;
    city: string | null;
  };
  identities: {
    id: string;
    type: string;
    value: string;
    verified: boolean;
    method: string;
    confidence: string;
  }[];
  accounts: { id: string; name: string; nameAr: string | null; role: string | null; isPrimary: boolean }[];
  cases: { id: string; number: string; subject: string; status: string; priority: string }[];
  deals: {
    id: string;
    title: string;
    amountMinor: string;
    currency: string;
    status: string;
    stageEn: string;
    stageAr: string;
  }[];
  tasks: TaskRow[];
  conversations: { id: string; channel: string; status: string; lastMessageAt: string | null }[];
}

function AddHandleDialog({ personId, onClose }: { personId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api(`/v1/people/${personId}/identities`, {
        method: 'POST',
        body: JSON.stringify({
          phones: phone.trim() ? [phone.trim()] : [],
          emails: email.trim() ? [email.trim()] : [],
        }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['person', personId] });
      onClose();
    },
  });
  const inUse = m.error instanceof ApiError && m.error.code === 'conflict';
  return (
    <Dialog
      open
      title={t('crm.person.addHandle')}
      onClose={onClose}
      onSubmit={() => m.mutate()}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-grad"
            disabled={m.isPending || (!phone.trim() && !email.trim())}
          >
            {t('common.add')}
          </button>
        </>
      }
    >
      <div className="form-grid">
        <Field id="ah-phone" label={t('crm.people.field.phone')}>
          <input
            id="ah-phone"
            className="input"
            dir="ltr"
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </Field>
        <Field id="ah-email" label={t('crm.people.field.email')}>
          <input
            id="ah-email"
            className="input"
            dir="ltr"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </Field>
      </div>
      {m.isError &&
        (inUse ? (
          <div className="form-error">{t('crm.person.handleInUse')}</div>
        ) : (
          <ErrorState error={m.error} />
        ))}
    </Dialog>
  );
}

export function PersonPage() {
  const { id = '' } = useParams();
  const { t, i18n } = useTranslation();
  const { can } = useAuth();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [dialog, setDialog] = useState<null | 'case' | 'deal' | 'task' | 'handle' | 'merge'>(null);
  const q = useQuery({ queryKey: ['person', id], queryFn: () => api<PersonDetail>(`/v1/people/${id}`) });
  const stage = useMutation({
    mutationFn: (lifecycleStage: string) =>
      api(`/v1/people/${id}`, { method: 'PATCH', body: JSON.stringify({ lifecycleStage }) }),
    onSuccess: () => CRM_KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState error={q.error} />;
  const { person, identities, accounts, cases, deals, tasks, conversations } = q.data;
  const ar = i18n.language === 'ar';
  const close = () => setDialog(null);

  return (
    <>
      {person.status === 'merged' && person.mergedInto && (
        <div className="notice" role="status" data-testid="merged-banner">
          {t('contacts.mergedBanner')}{' '}
          <Link to={`/people/${person.mergedInto}`}>{t('contacts.openSurvivor')}</Link>
        </div>
      )}
      <div className="record-head">
        <Avatar name={person.displayName} size={56} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 dir="auto">{person.displayName}</h1>
          <div className="sub">
            {[person.jobTitle, accounts[0] ? (ar && accounts[0].nameAr) || accounts[0].name : null]
              .filter(Boolean)
              .join(' · ')}
            {person.nameArabic && person.nameArabic !== person.displayName && (
              <>
                {person.jobTitle || accounts[0] ? ' · ' : ''}
                <bdi lang="ar">{person.nameArabic}</bdi>
              </>
            )}
          </div>
        </div>
        <label>
          <span className="sr-only">{t('crm.people.col.stage')}</span>
          <Select
            value={person.lifecycleStage}
            disabled={!can('persons:write')}
            onChange={(v) => stage.mutate(v)}
            aria-label={t('crm.people.col.stage')}
            data-testid="lifecycle"
            options={(['lead', 'contact', 'customer', 'churned'] as const).map((s) => ({
              value: s,
              label: t(`crm.lifecycle.${s}`),
            }))}
          />
        </label>
      </div>

      <div className="quick-actions" style={{ marginBottom: '1rem' }}>
        {can('cases:write') && (
          <button className="btn btn-ghost btn-sm" onClick={() => setDialog('case')}>
            <Briefcase size={14} />
            {t('crm.person.newCase')}
          </button>
        )}
        {can('deals:write') && (
          <button className="btn btn-ghost btn-sm" onClick={() => setDialog('deal')}>
            <Handshake size={14} />
            {t('crm.person.newDeal')}
          </button>
        )}
        {can('tasks:write') && (
          <button className="btn btn-ghost btn-sm" onClick={() => setDialog('task')}>
            <CheckSquare size={14} />
            {t('crm.person.newTask')}
          </button>
        )}
        {can('persons:write') && person.status !== 'merged' && (
          <button
            className="btn btn-ghost btn-sm"
            onClick={() => setDialog('merge')}
            data-testid="merge-with-open"
          >
            <GitMerge size={14} />
            {t('contacts.mergeWith.open')}
          </button>
        )}
      </div>

      <div className="record-grid">
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          <SectionBoundary resetKey={person.id}>
            <PurchasesPanel personId={person.id} />
          </SectionBoundary>
          <section className="card">
            <div className="card-head">
              <h2>{t('crm.person.timeline')}</h2>
            </div>
            <Timeline path={`/v1/people/${id}/timeline`} />
          </section>
        </div>
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          <section className="card" aria-label={t('crm.person.identities')}>
            <div className="card-head">
              <h2>{t('crm.person.identities')}</h2>
              {can('persons:write') && (
                <button
                  className="icon-btn"
                  onClick={() => setDialog('handle')}
                  aria-label={t('crm.person.addHandle')}
                  title={t('crm.person.addHandle')}
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
            <ul className="list-plain" data-testid="identities">
              {identities.map((i) => (
                <li key={i.id}>
                  <span className="channel-badge">
                    <ChannelIcon channel={IDENTITY_CHANNEL[i.type] ?? 'simulator'} />
                  </span>
                  <div className="grow">
                    <div
                      className="mono"
                      dir="ltr"
                      style={{ fontSize: 'var(--t-xs)', textAlign: 'start', overflowWrap: 'anywhere' }}
                    >
                      {handleLabel(t, i.type, i.value)}
                    </div>
                    <div className="faint" style={{ fontSize: '0.72rem' }}>
                      {t(`crm.person.linked.${i.method}`, { defaultValue: i.method })}
                    </div>
                  </div>
                  {i.verified ? (
                    <span className="badge badge-success" title={t('crm.person.verified')}>
                      <BadgeCheck size={12} />
                      {t('crm.person.verified')}
                    </span>
                  ) : (
                    <span className="badge badge-neutral">{t('crm.person.unverified')}</span>
                  )}
                </li>
              ))}
            </ul>
          </section>

          <ConsentPanel personId={person.id} identities={identities} />
          <MergeHistory personId={person.id} />
          {accounts.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h2>{t('crm.person.companies')}</h2>
              </div>
              <ul className="list-plain">
                {accounts.map((a) => (
                  <li key={a.id}>
                    <Link to={`/companies/${a.id}`} className="grow" dir="auto">
                      <strong>{(ar && a.nameAr) || a.name}</strong>
                    </Link>
                    {a.role && <span className="faint">{a.role}</span>}
                  </li>
                ))}
              </ul>
            </section>
          )}

          {deals.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h2>{t('crm.person.deals')}</h2>
              </div>
              <ul className="list-plain">
                {deals.map((d) => (
                  <li key={d.id}>
                    <div className="grow">
                      <div dir="auto">{d.title}</div>
                      <div className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                        {ar ? d.stageAr : d.stageEn}
                      </div>
                    </div>
                    <strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                      {fmt.money(Number(d.amountMinor), d.currency)}
                    </strong>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {cases.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h2>{t('crm.person.cases')}</h2>
              </div>
              <ul className="list-plain">
                {cases.map((c) => (
                  <li key={c.id}>
                    <Link to={`/cases/${c.id}`} className="grow" dir="auto">
                      #{c.number} {c.subject}
                    </Link>
                    <PriorityBadge value={c.priority} />
                    <StatusBadge value={c.status} ns="crm.caseStatus" />
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section className="card">
            <div className="card-head">
              <h2>{t('crm.person.tasks')}</h2>
            </div>
            <TaskList items={tasks} showRelated={false} />
          </section>

          {conversations.length > 0 && (
            <section className="card">
              <div className="card-head">
                <h2>{t('crm.person.conversations')}</h2>
              </div>
              <ul className="list-plain">
                {conversations.map((c) => (
                  <li key={c.id}>
                    <span className="channel-badge">
                      <ChannelIcon channel={c.channel} />
                      {t(`channels.${c.channel}`)}
                    </span>
                    <Link to={`/inbox?c=${c.id}`} className="grow" />
                    <span className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                      {c.lastMessageAt ? fmt.relative(c.lastMessageAt) : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <Notes relatedType="person" relatedId={id} />
        </div>
      </div>

      {dialog === 'case' && <NewCaseDialog open onClose={close} personId={id} accountId={accounts[0]?.id} />}
      {dialog === 'deal' && <NewDealDialog open onClose={close} personId={id} accountId={accounts[0]?.id} />}
      {dialog === 'task' && <NewTaskDialog open onClose={close} relatedType="person" relatedId={id} />}
      {dialog === 'handle' && <AddHandleDialog personId={id} onClose={close} />}
      {dialog === 'merge' && <MergeWithDialog personId={id} onClose={close} />}
    </>
  );
}
